/**
 * Upsell block: Add/Remove toggle, swatch updates (image, variant, title, price),
 * and currency-aware fixed discount pricing via Shopify currencies.js.
 * Listens for main product variant change and auto-selects the matching upsell colour.
 */

import { ThemeEvents } from '@theme/events';

/** @type {any} */
const CurrencyGlobal = typeof window !== 'undefined' ? window.Currency : undefined;

/**
 * Converts an option value to a handle (matches Liquid | handle).
 * @param {string} value - Option value e.g. "Light Blue"
 * @returns {string} Handle e.g. "light-blue" 
 */
function toHandle(value) {
  if (value == null || value === '') return '';
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/**
 * Formats cents with the cart currency symbol, without trailing zeros.
 * @param {number} cents - Price in cents
 * @param {string} currencySymbol - e.g. £, €, $
 * @returns {string}
 */
function formatMoneyWithoutTrailingZeros(cents, currencySymbol) {
  if (typeof cents !== 'number' || Number.isNaN(cents)) return '';

  const major = cents / 100;
  let amount;

  if (Math.abs(major - Math.round(major)) < 0.001) {
    amount = String(Math.round(major));
  } else {
    amount = major.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  }

  return (currencySymbol || '') + amount;
}

/**
 * @param {Element} block
 * @returns {{ cartCurrency: string, currencySymbol: string }}
 */
function getBlockPricingContext(block) {
  return {
    cartCurrency: block.getAttribute('data-cart-currency') || '',
    currencySymbol: block.getAttribute('data-cart-currency-symbol') || '',
  };
}

/**
 * Converts a fixed saving amount from upsellCurrency to the active cart currency.
 * @param {number} savingAmount - Saving in major currency units (e.g. 15 = £15)
 * @param {string} fromCurrency - ISO code e.g. GBP
 * @param {string} toCurrency - ISO code e.g. EUR
 * @returns {number}
 */
function convertSavingAmount(savingAmount, fromCurrency, toCurrency) {
  const from = (fromCurrency || '').toUpperCase();
  const to = (toCurrency || '').toUpperCase();

  if (!from || !to || from === to) {
    return savingAmount;
  }

  if (
    CurrencyGlobal &&
    typeof CurrencyGlobal.convert === 'function'
  ) {
    return CurrencyGlobal.convert(savingAmount, from, to);
  }

  return savingAmount;
}

/**
 * Calculates sale and compare prices for a swatch.
 * @param {Element} swatch - Swatch button
 * @param {string} cartCurrency - Active cart currency ISO code
 * @param {string} currencySymbol - Active cart currency symbol
 * @returns {{ saleFormatted: string, compareFormatted: string }}
 */
function calculateSwatchPrices(swatch, cartCurrency, currencySymbol) {
  const baseCents = parseInt(swatch.getAttribute('data-swatch-base-price') || '0', 10);
  const discountType = swatch.getAttribute('data-swatch-discount-type') || '';
  let saleCents = baseCents;

  if (discountType === 'percent') {
    const percent = parseFloat(swatch.getAttribute('data-swatch-percent') || '0');
    const discountCents = Math.round(baseCents * percent / 100);
    saleCents = Math.max(0, baseCents - discountCents);
  } else if (discountType === 'fixed') {
    const savingAmount = parseFloat(swatch.getAttribute('data-swatch-saving') || '0');
    const fromCurrency = swatch.getAttribute('data-swatch-currency') || cartCurrency || '';
    const convertedSaving = convertSavingAmount(savingAmount, fromCurrency, cartCurrency);
    const savingCents = Math.round(convertedSaving * 100);
    saleCents = Math.max(0, baseCents - savingCents);
  }

  return {
    saleFormatted: formatMoneyWithoutTrailingZeros(saleCents, currencySymbol),
    compareFormatted:
      saleCents < baseCents
        ? formatMoneyWithoutTrailingZeros(baseCents, currencySymbol)
        : '',
  };
}

/**
 * Updates displayed prices on an upsell block from swatch pricing data.
 * @param {Element} block - Upsell block element
 * @param {Element} swatch - Swatch button
 */
function applySwatchPrices(block, swatch) {
  const { cartCurrency, currencySymbol } = getBlockPricingContext(block);
  const { saleFormatted, compareFormatted } = calculateSwatchPrices(
    swatch,
    cartCurrency,
    currencySymbol
  );

  swatch.setAttribute('data-swatch-price', saleFormatted);
  swatch.setAttribute('data-swatch-compare-price', compareFormatted);

  const currentPriceEl = block.querySelector('[data-upsell-current-price]');
  const comparePriceEl = block.querySelector('[data-upsell-compare-price]');

  if (currentPriceEl && saleFormatted) {
    currentPriceEl.textContent = saleFormatted;
  }

  if (comparePriceEl instanceof HTMLElement) {
    if (compareFormatted) {
      comparePriceEl.textContent = compareFormatted;
      comparePriceEl.style.display = '';
    } else {
      comparePriceEl.textContent = '';
      comparePriceEl.style.display = 'none';
    }
  }
}

/**
 * Selects the matching swatch in an upsell block (variant, image, title, price, active state).
 * @param {Element} block - Upsell block element
 * @param {Element} swatch - Swatch button to select
 */
function selectUpsellSwatch(block, swatch) {
  const variantId = swatch.getAttribute('data-swatch-variant-id');
  const primary = swatch.getAttribute('data-swatch-primary');

  block.setAttribute('data-upsell-variant-id', variantId || '');

  const img = block.querySelector('[data-upsell-image]');
  if (img && primary && img instanceof HTMLImageElement) {
    img.src = primary;
    img.srcset = primary + ' 1x';
  }

  const swatchTitle = swatch.getAttribute('data-swatch-title');
  const titleElement = block.querySelector('[data-upsell-title]');
  if (titleElement && swatchTitle) {
    titleElement.textContent = swatchTitle;
  }

  const swatchLink = swatch.getAttribute('data-swatch-url');
  const linkElement = block.querySelector('a.upsell-block__image-link');
  if (linkElement && swatchLink) {
    linkElement.setAttribute('href', swatchLink);
  }

  applySwatchPrices(block, swatch);

  block.querySelectorAll('.custom-product-card__swatch').forEach(function (s) {
    s.classList.remove('is-active');
  });
  swatch.classList.add('is-active');

  const available = swatch.getAttribute('data-swatch-available');
  block.setAttribute('data-upsell-available', available === 'true' ? 'true' : 'false');
  updateUpsellCtaAvailability(block);
}

/**
 * Updates the upsell CTA text, disabled state and classes based on variant availability.
 * @param {Element} block - Upsell block element
 */
function updateUpsellCtaAvailability(block) {
  const cta = block.querySelector('[data-upsell-cta]');
  const ctaText = block.querySelector('[data-upsell-cta-text]');
  if (!cta || !ctaText) return;

  const addText = cta.getAttribute('data-add-text');
  const removeText = cta.getAttribute('data-remove-text');
  const outOfStockText = cta.getAttribute('data-out-of-stock-text');
  const available = block.getAttribute('data-upsell-available') === 'true';

  if (!available) {
    ctaText.textContent = outOfStockText || 'Out of stock';
    cta.classList.remove('custom-button--dark', 'custom-button--light');
    cta.classList.add('add-to-cart-button', 'button');
    cta.setAttribute('disabled', '');
  } else {
    const isAdd = block.getAttribute('data-upsell-add') !== 'true';
    ctaText.textContent = isAdd ? addText : removeText;
    cta.classList.remove('add-to-cart-button', 'button');
    cta.classList.remove('custom-button--dark', 'custom-button--light');
    cta.classList.add(isAdd ? 'custom-button--dark' : 'custom-button--light');
    cta.removeAttribute('disabled');
  }
}

/**
 * Applies currency-aware prices to all swatches and the active swatch display.
 * @param {Element} block - Upsell block element
 */
function refreshUpsellBlockPrices(block) {
  const { cartCurrency, currencySymbol } = getBlockPricingContext(block);
  const swatches = block.querySelectorAll('.custom-product-card__swatch[data-swatch-variant-id]');

  swatches.forEach(function (swatch) {
    if (!(swatch instanceof HTMLElement)) return;

    const { saleFormatted, compareFormatted } = calculateSwatchPrices(
      swatch,
      cartCurrency,
      currencySymbol
    );
    swatch.setAttribute('data-swatch-price', saleFormatted);
    swatch.setAttribute('data-swatch-compare-price', compareFormatted);
  });

  const activeSwatch =
    block.querySelector('.custom-product-card__swatch.is-active[data-swatch-variant-id]') ||
    block.querySelector('.custom-product-card__swatch[data-swatch-variant-id]');

  if (activeSwatch instanceof HTMLElement) {
    applySwatchPrices(block, activeSwatch);
  }
}

function initUpsellBlocks() {
  document.querySelectorAll('[data-upsell-block]').forEach(function (block) {
    const cta = block.querySelector('[data-upsell-cta]');
    const ctaText = block.querySelector('[data-upsell-cta-text]');
    const addText = cta && cta.getAttribute('data-add-text');
    const removeText = cta && cta.getAttribute('data-remove-text');

    refreshUpsellBlockPrices(block);
    updateUpsellCtaAvailability(block);

    if (cta && ctaText && addText && removeText) {
      const card = block.querySelector('.upsell-block__card');
      cta.addEventListener('click', function () {
        if (cta.hasAttribute('disabled')) return;
        const isAdd = block.getAttribute('data-upsell-add') !== 'true';
        block.setAttribute('data-upsell-add', isAdd ? 'true' : 'false');
        ctaText.textContent = isAdd ? removeText : addText;
        cta.classList.remove('custom-button--dark', 'custom-button--light');
        cta.classList.add(isAdd ? 'custom-button--light' : 'custom-button--dark');
        if (card) {
          if (isAdd) {
            card.classList.add('upsell-block__card--border-active');
          } else {
            card.classList.remove('upsell-block__card--border-active');
          }
        }
      });
    }

    block.querySelectorAll('.custom-product-card__swatch[data-swatch-variant-id]').forEach(function (swatch) {
      if (swatch.tagName !== 'BUTTON') return;
      swatch.addEventListener('click', function () {
        selectUpsellSwatch(block, swatch);
      });
    });
  });
}

/**
 * Waits for Shopify currencies.js before initializing price conversion.
 * @param {() => void} callback
 */
function whenCurrencyReady(callback) {
  if (CurrencyGlobal && typeof CurrencyGlobal.convert === 'function') {
    callback();
    return;
  }

  const existingScript = document.querySelector('script[src*="currencies.js"]');

  if (existingScript) {
    existingScript.addEventListener('load', callback, { once: true });
    return;
  }

  callback();
}

/**
 * On main product variant change, auto-select the upsell swatch that matches the new colour.
 * @param {CustomEvent & { detail?: { resource?: { option1?: string, option2?: string, option3?: string } } }} event - Variant update event
 */
function onVariantUpdate(event) {
  const variant = event.detail?.resource;
  if (!variant) return;

  document.querySelectorAll('[data-upsell-block]').forEach(function (block) {
    if (block.getAttribute('data-upsell-awaiting-variant') === 'true') {
      block.removeAttribute('data-upsell-awaiting-variant');
      return;
    }

    const index = block.getAttribute('data-main-colour-option-index') || '1';
    const colourValue = variant['option' + index];
    if (colourValue == null) return;

    const colourHandle = toHandle(colourValue);
    if (!colourHandle) return;

    const parts = colourHandle.split('-');
    const tryHandles = [];
    tryHandles.push(colourHandle);
    for (let i = 0; i < parts.length - 1; i++) {
      tryHandles.push(parts[i] + '-' + parts[i + 1]);
    }
    for (let i = 0; i < parts.length; i++) {
      if (tryHandles.indexOf(parts[i]) === -1) {
        tryHandles.push(parts[i]);
      }
    }

    for (let i = 0; i < tryHandles.length; i++) {
      const tryHandle = tryHandles[i];
      const match = block.querySelector(
        '.custom-product-card__swatch[data-swatch-colour="' + tryHandle + '"]'
      );
      if (match && match.tagName === 'BUTTON') {
        selectUpsellSwatch(block, match);
        break;
      }
    }
  });
}

document.addEventListener(ThemeEvents.variantUpdate, onVariantUpdate);

function bootUpsellBlocks() {
  whenCurrencyReady(initUpsellBlocks);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootUpsellBlocks);
} else {
  bootUpsellBlocks();
}

document.addEventListener('section:rendered', bootUpsellBlocks);
